import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import IntegritasLogoS from '../../assets/logos/IntegritasLogoS';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isHistoryActive = () => isActive('/stamps') || isActive('/verifications');

  const handleNavigate = (url: string) => {
    navigate(url);
    onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 overflow-hidden">
      <div className="flex flex-col h-full">
        {/* Header with Logo and Close Button */}
        <div className="flex justify-between items-center p-4 border-b border-gray-800">
          <div className="flex items-center">
            <IntegritasLogoS />
          </div>
          <button
            onClick={onClose}
            className="text-white p-2 hover:text-minima-orange-2023 transition-colors"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Navigation Content  */}
        <div className="flex-1 overflow-y-auto">
          <nav className="px-6 py-6">
            <ul className="space-y-2 text-lg font-normal">
              <li>
                <button
                  onClick={() => handleNavigate('/stamp')}
                  className={`w-full text-left py-3 ${
                    isActive('/stamp')
                      ? 'text-minima-orange-2023'
                      : 'text-white hover:text-core-grey-80'
                  }`}
                >
                  Stamp
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/analyze')}
                  className={`w-full text-left py-3 ${
                    isActive('/analyze')
                      ? 'text-minima-orange-2023'
                      : 'text-white hover:text-core-grey-80'
                  }`}
                >
                  Analyze
                </button>
              </li>
              <li>
                <button
                  onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                  className={`w-full text-left flex items-center justify-between py-3 ${
                    isHistoryActive()
                      ? 'text-minima-orange-2023'
                      : 'text-white hover:text-core-grey-80'
                  }`}
                >
                  History
                  <svg
                    className={`w-5 h-5 transition-transform ${
                      isHistoryExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isHistoryExpanded && (
                  <div className="ml-4 mt-2 space-y-2">
                    <button
                      onClick={() => handleNavigate('/stamps')}
                      className={`w-full text-left text-base py-2 ${
                        isActive('/stamps')
                          ? 'text-minima-orange-2023'
                          : 'text-white hover:text-core-grey-80'
                      }`}
                    >
                      Stamps
                    </button>
                    <button
                      onClick={() => handleNavigate('/verifications')}
                      className={`w-full text-left text-base py-2 ${
                        isActive('/verifications')
                          ? 'text-minima-orange-2023'
                          : 'text-white hover:text-core-grey-80'
                      }`}
                    >
                      Verifications
                    </button>
                  </div>
                )}
              </li>
              <li>
                <button
                  onClick={() => handleNavigate('/about')}
                  className={`w-full text-left py-3 ${
                    isActive('/about')
                      ? 'text-minima-orange-2023'
                      : 'text-white hover:text-core-grey-80'
                  }`}
                >
                  About
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Footer with Logout */}
        {isAuthenticated && (
          <div className="p-6 border-t border-gray-800">
            <button
              onClick={handleLogout}
              className="w-full text-left text-white hover:text-minima-orange-2023 transition-colors py-3"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileMenu;
